import { motion } from 'framer-motion';
import { useEffect, useRef } from 'react';
import './AIOrb.css';

export interface AIOrbProps {
  isTalking?: boolean;
  isListening?: boolean;
  size?: number;
  iconSize?: number;
}

type StateKey = 'idle' | 'listening' | 'talking';

interface StateConfig {
  rotateSpeed: number;
  counterRotateSpeed: number;
  haloPulseDuration: number;
  haloMaxScale: number;
  orbScale: number;
  orbPulse: boolean;
}

const STATE_CONFIG: Record<StateKey, StateConfig> = {
  idle: {
    rotateSpeed: 14,
    counterRotateSpeed: 11,
    haloPulseDuration: 3.5,
    haloMaxScale: 1.08,
    orbScale: 1,
    orbPulse: false,
  },
  listening: {
    rotateSpeed: 6,
    counterRotateSpeed: 4.5,
    haloPulseDuration: 1.7,
    haloMaxScale: 1.14,
    orbScale: 1.04,
    orbPulse: false,
  },
  talking: {
    rotateSpeed: 3,
    counterRotateSpeed: 2.2,
    haloPulseDuration: 0.9,
    haloMaxScale: 1.2,
    orbScale: 1.05,
    orbPulse: true,
  },
};

const AIOrb: React.FC<AIOrbProps> = ({
  isTalking = false,
  isListening = false,
  size = 240,
  iconSize = 40,
}) => {
  let state: StateKey = 'idle';
  if (isTalking) state = 'talking';
  else if (isListening) state = 'listening';

  const auraRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef(0);
  const auraRSmooth = useRef(0);
  const auraOpSmooth = useRef(0);
  const simVolume = useRef(0);

  // Factor de escala: 240px es la base de diseño
  const scale = size / 240;
  const haloBlur = Math.max(2, 20 * scale);
  const layerBlur = Math.max(3, 25 * scale);
  const layer2Blur = Math.max(2, 18 * scale);
  const highlightBlur = Math.max(1, 6 * scale);
  const innerShadow = Math.max(4, 40 * scale);
  const innerShadow2 = Math.max(3, 12 * scale);
  const innerShadowSpread = Math.max(4, 35 * scale);
  const outerGlow = Math.max(6, 60 * scale);
  const outerGlowTalking = Math.max(8, 80 * scale);
  const auraMaxRadius = Math.max(10, 70 * scale);

  const cssVars: Record<string, string | number> = {
    '--orb-halo-blur': `${haloBlur}px`,
    '--orb-layer-blur': `${layerBlur}px`,
    '--orb-layer2-blur': `${layer2Blur}px`,
    '--orb-highlight-blur': `${highlightBlur}px`,
    '--orb-inner-shadow': `${innerShadow}px`,
    '--orb-inner-shadow-2': `${innerShadow2}px`,
    '--orb-inner-shadow-spread': `${innerShadowSpread}px`,
    '--orb-outer-glow': `${outerGlow}px`,
    '--orb-outer-glow-talking': `${outerGlowTalking}px`,
    '--orb-icon-size': `${iconSize}%`,
    width: size,
    height: size,
  };

  // Loop del aura simulada
  useEffect(() => {
    const auraColor = { r: 165, g: 180, b: 252 };
    const LERP_SMOOTH = 0.12;
    const t0 = performance.now();

    function tick() {
      const elapsed = (performance.now() - t0) / 1000;

      let targetVolume: number;
      if (isTalking) {
        targetVolume =
          0.5 +
          0.4 *
            Math.abs(Math.sin(elapsed * 6)) *
            (0.6 + 0.4 * Math.sin(elapsed * 2.3));
      } else if (isListening) {
        targetVolume = 0.18 + 0.1 * Math.sin(elapsed * 4);
      } else {
        targetVolume = 0;
      }

      simVolume.current += (targetVolume - simVolume.current) * 0.25;

      const active = isTalking || isListening;
      const targetR = active ? simVolume.current * auraMaxRadius : 0;
      const targetOp = active ? Math.min(1, simVolume.current * 1.5) : 0;

      auraRSmooth.current += (targetR - auraRSmooth.current) * LERP_SMOOTH;
      auraOpSmooth.current += (targetOp - auraOpSmooth.current) * LERP_SMOOTH;

      const R = auraRSmooth.current;
      const op = auraOpSmooth.current;

      if (auraRef.current) {
        if (R > 0.5) {
          auraRef.current.style.boxShadow = `0 0 ${R.toFixed(1)}px ${(
            R * 0.45
          ).toFixed(1)}px rgba(${auraColor.r},${auraColor.g},${
            auraColor.b
          },${op.toFixed(2)})`;
        } else {
          auraRef.current.style.boxShadow = 'none';
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    }

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [isTalking, isListening, auraMaxRadius]);

  const config = STATE_CONFIG[state];

  const bodyAnimate = config.orbPulse
    ? { scale: [config.orbScale, config.orbScale * 1.06, config.orbScale] }
    : { scale: config.orbScale };

  const bodyTransition = config.orbPulse
    ? { duration: 0.8, repeat: Infinity, ease: 'easeInOut' }
    : { duration: 0.6, ease: 'easeOut' };

  return (
    <div
      className={`orb-scene state-${state}`}
      style={cssVars as React.CSSProperties}
    >
      {/* Aura reactiva (debajo de todo) */}
      <div ref={auraRef} className="orb-aura" />

      {/* Halo difuso pulsante */}
      <motion.div
        className="orb-halo"
        animate={{
          scale: [1, config.haloMaxScale, 1],
          opacity: [0.7, 1, 0.7],
        }}
        transition={{
          duration: config.haloPulseDuration,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Cuerpo del orb */}
      <motion.div
        className="orb-body"
        animate={bodyAnimate}
        transition={bodyTransition}
      >
        <motion.div
          className="orb-layer-1"
          animate={{ rotate: 360 }}
          transition={{
            duration: config.rotateSpeed,
            repeat: Infinity,
            ease: 'linear',
          }}
        />
        <motion.div
          className="orb-layer-2"
          animate={{ rotate: -360 }}
          transition={{
            duration: config.counterRotateSpeed,
            repeat: Infinity,
            ease: 'linear',
          }}
        />
        <div className="orb-highlight" />
      </motion.div>

      {/* Waveform: visible en idle (suave) y talking (intenso), oculto en listening */}
      <div className="orb-waveform" aria-hidden="true">
        <div className="wave-bar" />
        <div className="wave-bar" />
        <div className="wave-bar" />
        <div className="wave-bar" />
        <div className="wave-bar" />
      </div>
    </div>
  );
};

export default AIOrb;
